import React from 'react';
import CompareShell, { AppStoreBadges } from './CompareShell';
import { Check, X, Users, Receipt, Zap, ArrowRight } from 'lucide-react';

const SPLITWISE_MATRIX = [
  {
    category: 'Clero Groups & Shared Spending',
    features: [
      {
        name: 'Monthly roommate groups (rent & bills)',
        clero: 'Auto-settles on the 1st',
        competitor: 'Manual reminders only',
        cleroWinner: true,
      },
      {
        name: 'Trip & vacation groups',
        clero: 'Auto-settles on trip end date',
        competitor: 'Manual ledger only',
        cleroWinner: true,
      },
      {
        name: 'On-demand running group tabs',
        clero: '1-tap settle anytime',
        competitor: 'Manual IOU list',
        cleroWinner: true,
      },
      {
        name: 'Group balance simplification',
        clero: 'Included ($0 free)',
        competitor: '$39.99/yr (Splitwise Pro)',
        cleroWinner: true,
      },
      {
        name: '1-tap batch settlement for all tabs',
        clero: '1 bank transfer',
        competitor: 'Separate 1-by-1 payments',
        cleroWinner: true,
      },
      {
        name: 'Locked payment records',
        clero: 'Permanently locked',
        competitor: 'Past edits permitted',
        cleroWinner: true,
      },
    ],
  },
  {
    category: 'Receipts & Item Splitting',
    features: [
      {
        name: 'AI camera receipt scanner',
        clero: 'Free & unlimited',
        competitor: '$39.99/yr Pro only',
        cleroWinner: true,
      },
      {
        name: 'Gmail & email receipt sync',
        clero: 'Auto-finds Uber & food delivery',
        competitor: 'Not supported',
        cleroWinner: true,
      },
      {
        name: 'Friend web claiming (no app needed)',
        clero: '1-tap on web link',
        competitor: 'Requires app install',
        cleroWinner: true,
      },
      {
        name: 'Smart card charge requests',
        clero: '1-tap from transactions',
        competitor: 'Manual typing required',
        cleroWinner: true,
      },
      {
        name: 'Exact cent tax & tip split',
        clero: 'Proportional',
        competitor: 'Supported',
        cleroWinner: false,
      },
    ],
  },
  {
    category: 'Money Movement & Bank Settlement',
    features: [
      {
        name: 'Direct bank checking deposit',
        clero: 'Same-day bank transfer*',
        competitor: 'Not supported (tracking only)',
        cleroWinner: true,
      },
      {
        name: 'Transfer & cashout fees',
        clero: '$0 always',
        competitor: 'N/A (no payments)',
        cleroWinner: true,
      },
      {
        name: 'Bank-verified confirmation',
        clero: 'Automated bank confirmation',
        competitor: 'Manual checkbox',
        cleroWinner: true,
      },
      {
        name: 'Web payment portal for friends',
        clero: 'Pay on web without app',
        competitor: 'Requires account & app',
        cleroWinner: true,
      },
    ],
  },
  {
    category: 'Pricing & User Experience',
    features: [
      {
        name: 'Annual subscription fee',
        clero: '$0 (100% free)',
        competitor: '$39.99 per year',
        cleroWinner: true,
      },
      {
        name: 'Daily expense entry limit',
        clero: 'Unlimited',
        competitor: '3 entries/day limit',
        cleroWinner: true,
      },
      {
        name: 'Ad-free interface',
        clero: '100% ad-free',
        competitor: 'Banner & popup ads',
        cleroWinner: true,
      },
      {
        name: 'Privacy',
        clero: 'Private by default',
        competitor: 'Group private',
        cleroWinner: false,
      },
    ],
  },
];

export const CompareSplitwisePage: React.FC = () => {
  const navigate = (path: string) => {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
    window.scrollTo(0, 0);
  };

  return (
    <CompareShell competitorName="Splitwise" competitorSlug="splitwise">
      {/* HERO SECTION */}
      <section className="pt-6 pb-12 sm:pb-16 px-4 sm:px-6">
        <div className="mx-auto max-w-4xl text-center space-y-4">
          <h1 className="text-4xl sm:text-6xl font-black tracking-tight text-slate-950">
            Clero vs Splitwise
          </h1>
          <p className="mx-auto max-w-xl text-base sm:text-lg text-slate-600 font-medium">
            See how Clero compares to Splitwise for everyday and group payments.
          </p>

          {/* HERO DOWNLOAD ACTION (FLIGHTY STYLE) */}
          <div className="pt-2 flex items-center justify-center">
            <AppStoreBadges badgeHeightClass="h-11 sm:h-12" />
          </div>
        </div>
      </section>

      {/* FULL COMPARISON MATRIX SECTION */}
      <section className="py-8 px-4 sm:px-6">
        <div className="mx-auto max-w-5xl">
          {/* COMPREHENSIVE FLIGHTY-STYLE WRITE-UP */}
          <div className="rounded-3xl bg-white p-8 sm:p-12 mb-10 space-y-8">
            <div className="border-b border-slate-100 pb-6">
              <h2 className="text-2xl sm:text-3xl font-black text-slate-950 tracking-tight">
                Moving from offline IOU tracking to real-world group banking
              </h2>
              <p className="mt-3 text-base text-slate-600 font-medium leading-relaxed">
                Splitwise popularized shared expense tracking over a decade ago by creating a shared digital ledger. But calculating what people owe is only the first half of the problem. Collecting real money without friction is where group finances usually break down.
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-3">
              <div className="space-y-2.5">
                <h3 className="text-base font-bold text-slate-950">1. Real bank settlement vs manual chasing</h3>
                <p className="text-sm text-slate-600 leading-relaxed font-medium">
                  Splitwise does not move money. You log a dinner, then message friends to pay you on Venmo or Zelle, and manually return to Splitwise to check off boxes. Clero moves funds directly from checking account to checking account on the same day*, locking records permanently once paid.
                </p>
              </div>

              <div className="space-y-2.5">
                <h3 className="text-base font-bold text-slate-950">2. Free AI scanning vs $39.99/yr paywalls</h3>
                <p className="text-sm text-slate-600 leading-relaxed font-medium">
                  Splitwise locks essential utilities behind a $39.99/year subscription, including receipt OCR, currency conversions, and adding more than 3 expenses a day. Clero includes unlimited AI camera receipt scanning, Gmail receipt sync, and debt minimization completely free ($0).
                </p>
              </div>

              <div className="space-y-2.5">
                <h3 className="text-base font-bold text-slate-950">3. Purpose-built group cycles</h3>
                <p className="text-sm text-slate-600 leading-relaxed font-medium">
                  Generic ledger lists become cluttered over time. Clero organizes shared life into dedicated group modes: <strong>Monthly</strong> roommate cycles that auto-settle on the 1st, <strong>Trip</strong> groups for vacations, and <strong>On-Demand</strong> running tabs with 1-tap batch settlements.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center max-w-2xl mx-auto mb-8">
            <h2 className="text-2xl sm:text-3xl font-black text-slate-950 tracking-tight">
              Feature breakdown
            </h2>
            <p className="mt-2 text-sm text-slate-600 font-medium">
              Side-by-side comparison of daily payment and group expense capabilities.
            </p>
          </div>

          <div className="overflow-hidden rounded-3xl bg-white">
            {/* TABLE TOP HEADER WITH APP LOGOS */}
            <div className="grid grid-cols-12 gap-3 px-6 py-4 border-b border-slate-100 items-center bg-slate-50/50">
              <div className="col-span-6 sm:col-span-5">
                <span className="text-xs font-extrabold uppercase tracking-wider text-slate-400">Features</span>
              </div>
              <div className="col-span-3 sm:col-span-3.5 flex items-center gap-2 px-3 py-1.5">
                <img src="/clero_icon.png" alt="Clero" className="h-6 w-6 rounded-lg shrink-0 object-cover shadow-sm" />
                <span className="font-extrabold text-teal-950 text-xs sm:text-sm">Clero</span>
              </div>
              <div className="col-span-3 sm:col-span-3.5 flex items-center gap-2 px-3 py-1.5">
                <img src="/logos/splitwise.svg" alt="Splitwise" className="h-6 w-6 rounded-lg shrink-0 shadow-sm" />
                <span className="font-bold text-slate-800 text-xs sm:text-sm">Splitwise</span>
              </div>
            </div>

            {/* MATRIX CATEGORIES & CONCISE ROWS */}
            {SPLITWISE_MATRIX.map((section) => (
              <div key={section.category}>
                <div className="bg-slate-100/70 px-6 py-2.5">
                  <h3 className="text-[11px] font-extrabold uppercase tracking-wider text-slate-800">
                    {section.category}
                  </h3>
                </div>

                <div className="divide-y divide-slate-100">
                  {section.features.map((row) => (
                    <div
                      key={row.name}
                      className="grid grid-cols-12 gap-3 px-6 py-3.5 items-center hover:bg-slate-50/50 transition-colors"
                    >
                      <div className="col-span-6 sm:col-span-5">
                        <span className="text-xs sm:text-sm font-bold text-slate-900 block">{row.name}</span>
                      </div>

                      <div className="col-span-3 sm:col-span-3.5 flex items-center gap-2 bg-teal-50/40 p-2 rounded-xl">
                        <div className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-teal-600 text-white">
                          <Check className="h-2.5 w-2.5 stroke-[3]" />
                        </div>
                        <span className="text-xs font-bold text-teal-950">{row.clero}</span>
                      </div>

                      <div className="col-span-3 sm:col-span-3.5 flex items-center gap-2 p-2">
                        {row.cleroWinner ? (
                          <div className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                            <X className="h-2.5 w-2.5 stroke-[2.5]" />
                          </div>
                        ) : (
                          <div className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-slate-200 text-slate-600">
                            <Check className="h-2.5 w-2.5 stroke-[3]" />
                          </div>
                        )}
                        <span className="text-xs font-medium text-slate-600">{row.competitor}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* MID-PAGE CONVERSION CALLOUT (FLIGHTY STYLE) */}
          <div className="mt-8 rounded-3xl bg-white p-6 sm:p-8 text-center space-y-3">
            <h3 className="text-lg sm:text-xl font-black text-slate-950">
              Ready for simpler group payments?
            </h3>
            <p className="text-xs sm:text-sm text-slate-600 max-w-md mx-auto">
              Direct bank deposits, AI receipt scanning, and group balance netting with $0 fees.
            </p>
            <div className="flex justify-center pt-1">
              <AppStoreBadges badgeHeightClass="h-10 sm:h-11" />
            </div>
          </div>
        </div>
      </section>

      {/* FREQUENTLY ASKED QUESTIONS (FLIGHTY STYLE) */}
      <section className="py-8 px-4 sm:px-6">
        <div className="mx-auto max-w-5xl rounded-3xl bg-white p-7 sm:p-10">
          <h2 className="text-2xl sm:text-3xl font-black text-slate-950 tracking-tight">
            Frequently asked questions
          </h2>
          <div className="mt-6 space-y-6 text-sm sm:text-base text-slate-700 font-medium divide-y divide-slate-100">
            <div className="pt-4 first:pt-0">
              <h3 className="text-base font-bold text-slate-950">Do my friends need to download Clero to pay or claim expenses?</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                No. When you share a Clero expense or group tab, friends can simply open the secure web link in any browser, claim the exact items they ordered, and pay directly without installing an app or registering an account.
              </p>
            </div>
            <div className="pt-4">
              <h3 className="text-base font-bold text-slate-950">Is AI receipt scanning really free and unlimited?</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                Yes. Unlike Splitwise Pro ($39.99/year), Clero includes unlimited AI camera receipt scanning and automated Gmail receipt sync at zero cost. Line items, taxes, and tips are itemized automatically.
              </p>
            </div>
            <div className="pt-4">
              <h3 className="text-base font-bold text-slate-950">How does Clero simplify group balances?</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                Clero automatically calculates the minimal number of bank transfers needed to settle any group tab. If multiple people paid for different dinners and groceries, Clero combines all debts so each person only makes or receives one clean payment.
              </p>
            </div>
            <div className="pt-4">
              <h3 className="text-base font-bold text-slate-950">Why is Clero free with zero subscription fees?</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                Clero uses direct bank-to-bank connections via Plaid and Astra Financial, avoiding high credit card processing fees and middleman wallet holding infrastructure.
              </p>
            </div>
            <div className="pt-4">
              <h3 className="text-base font-bold text-slate-950">Can anyone edit past group expenses after they are paid?</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                No. In Clero, once an expense share is paid or verified, the record is permanently locked to protect everyone in the group from retrospective changes and calculation drift.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* OTHER COMPARISONS QUICK JUMP */}
      <section className="py-8 px-4 sm:px-6">
        <div className="mx-auto max-w-4xl text-center space-y-4">
          <span className="text-xs font-extrabold tracking-widest uppercase text-slate-500">
            More comparisons
          </span>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => navigate('/compare/venmo')}
              className="inline-flex items-center gap-2 rounded-2xl bg-white px-5 py-3 text-sm font-bold text-slate-900 hover:text-teal-700 transition-colors"
            >
              <span>Clero vs Venmo</span>
              <ArrowRight className="h-4 w-4 text-teal-600" />
            </button>
            <button
              onClick={() => navigate('/compare/zelle')}
              className="inline-flex items-center gap-2 rounded-2xl bg-white px-5 py-3 text-sm font-bold text-slate-900 hover:text-teal-700 transition-colors"
            >
              <span>Clero vs Zelle</span>
              <ArrowRight className="h-4 w-4 text-teal-600" />
            </button>
          </div>
        </div>
      </section>
    </CompareShell>
  );
};
export default CompareSplitwisePage;
